import { Injectable, computed, signal } from '@angular/core';
import { ProductModel, ReturnItemModel } from '@app/models';

@Injectable({
    providedIn: 'root'
})
export class ReturnService
{
    private readonly DEFAULT_REASON = 1;
    // Computed signals for reactive cart data
    items = computed(() => this.returnItems());
    itemCount = computed(() => this.returnItems().reduce((total, item) => total + item.quantity, 0));
    totalPrice = computed(() => this.returnItems().reduce((total, item) => total + item.subtotal, 0));

    private returnItems = signal<ReturnItemModel[]>([]);

    addToReturn(product: ProductModel, quantity: number = 1, returnReasonId?: number): void
    {
        const currentItems = this.returnItems();
        const existingItemIndex = currentItems.findIndex((item) => item.product.id === product.id);

        if (existingItemIndex >= 0)
        {
            const updatedQuantity = currentItems[existingItemIndex].quantity + quantity;
            const updatedItems = [...currentItems];
            updatedItems[existingItemIndex] = {
                ...currentItems[existingItemIndex],
                quantity: updatedQuantity,
                return_reason_id: returnReasonId || currentItems[existingItemIndex].return_reason_id,
                subtotal: updatedQuantity * (product.price ? Number(product.price) : 0),
            };
            this.returnItems.set(updatedItems);
        }
        else
        {
            // Add new item
            const newItem: ReturnItemModel = {
                product,
                quantity,
                return_reason_id: returnReasonId || this.DEFAULT_REASON,
                subtotal: quantity * (product.price ? Number(product.price) : 0),
            };
            this.returnItems.set([...currentItems, newItem]);
        }
    }

    removeFromReturn(productId: number): void
    {
        const updatedItems = this.returnItems().filter(item => item.product.id !== productId);
        this.returnItems.set(updatedItems);
    }

    updateReason(productId: number, returnReasonId: number): void
    {
        const currentItems = this.returnItems();
        const existingItemIndex = currentItems.findIndex((item) => item.product.id === productId);

        if (existingItemIndex >= 0)
        {
            const updatedItems = [...currentItems];
            updatedItems[existingItemIndex] = {
                ...updatedItems[existingItemIndex],
                return_reason_id: returnReasonId,
            };
            this.returnItems.set(updatedItems);
        }
    }

    getItemReason(productId: number): number
    {
        const item = this.returnItems().find((item) => item.product.id === productId);

        return item ? item.return_reason_id : this.DEFAULT_REASON;
    }

    updateQuantity(productId: number, quantity: number): void
    {
        if (quantity <= 0)
        {
            this.removeFromReturn(productId);

            return;
        }

        const currentItems = this.returnItems();
        const existingItemIndex = currentItems.findIndex((item) => item.product.id === productId);

        if (existingItemIndex >= 0)
        {
            const updatedItems = [...currentItems];
            const updatedPrice = updatedItems[existingItemIndex].product.price;
            const updatedSubtotal = quantity * (updatedPrice ? Number(updatedPrice) : 0);
            updatedItems[existingItemIndex] = {
                ...updatedItems[existingItemIndex],
                quantity,
                subtotal: updatedSubtotal,
            };
            this.returnItems.set(updatedItems);
        }
    }

    getItemSubtotal(productId: number): number
    {
        const item = this.returnItems().find((item) => item.product.id === productId);

        return item ? item.subtotal : 0;
    }

    getItemQuantity(productId: number): number
    {
        const item = this.returnItems().find(item => item.product.id === productId);

        return item ? item.quantity : 0;
    }

    clearReturn(): void
    {
        this.returnItems.set([]);
    }

    isInReturn(productId: number): boolean
    {
        return this.returnItems().some((item) => item.product.id === productId);
    }
}
